<!-- Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. -->

<!--task-edit.component.html-->
<div
	[shakeAnimation]="makeShake"
	class="light-container light-container--border-radius ticket-edit-container"
	[formGroup]="editForm"
>
	<div class="ticket-message-grid">
		<ticket-message [message]="data.messageString"></ticket-message>
	</div>
	<!-- CONTENT UNIQUE TO TICKET TYPE -->
	<!-- Single ticket edit -->

	<div class="ticket-edit-container-box task-viewport">
		<div class="ticket-edit-container__content">
			<!-- Need to show this when the task has been actualized and then toggled back to planned view -->
			<div *ngIf="isPlannedPreview" class="ticket-edit-container__planned-watermark-wrapper">
				<div class="ticket-edit-container__planned-watermark" automationId="taskPlannedWatermark">
					PLANNED
				</div>
			</div>
			<div class="ticket-edit-container__header">
				<ticket-thumbnail
					[ticketType]="data.selectedType"
					[ticketRole]="data.data.liveRole"
					[isComplete]="data.data.isActualized"
					[isStarted]="isStarted"
				>
				</ticket-thumbnail>
				<div class="ticket-edit-container__header-date ticket-edit-container__task-header-date">
					<ticket-calendar
						*ngIf="data.ticketsInPlanRegion"
						[open$]="calendarController$"
						formControlName="liveDateRange"
						[editForm]="editForm"
						[readOnly]="(planAccess$ | async) === PlanAccessLevel.archived"
					></ticket-calendar>
				</div>
				<div class="ticket-edit-container__header-pin">
					<ticket-promise-icon
						class="legacy-pin-margin-centering-fix"
						[totalPins]="data.data.livePromisePeriodStack ? data.data.livePromisePeriodStack.length : 0"
						[disabled]="variancePromptOpen"
						[missedCount]="data.data.livePromisePeriodStack ? data.data.livePromisePeriodStack.length - 1 : 0"
						[color]="
							data.data.livePromisePeriodStack
								? data.data.livePromisePeriodStack[data.data.livePromisePeriodStack.length - 1].data.color
								: '#000000'
						"
						[ticketType]="data.selectedType"
						(clicked)="openPinHistory($event)"
					>
					</ticket-promise-icon>
				</div>
				<div class="ticket-edit-container__hamburger-menu">
					<ticket-action-menu
						automationId="ticketHamburgerMenu"
						[editTicketCombined]="data"
					></ticket-action-menu>
				</div>
			</div>
			<div class="ticket-edit-container__role dropdown-set-width">
				<ticket-role
					formControlName="roleId"
					automationId="roleId"
					[roleList]="planState.rolesApi.data.list$"
					[projectMember]="projectMember$ | async"
				>
				</ticket-role>
			</div>
			<div class="ticket-edit-container__location dropdown-set-width">
				<ticket-location
					formControlName="locationId"
					automationId="locationId"
					[locationList]="planState.locationsApi.data.list$"
				></ticket-location>
			</div>
			<div class="ticket-edit-container__description-container grid-ticket-subcomponent-wrapper">
				<ticket-description
					class="grid-ticket-subcomponent-wrapper__input"
					[shouldFocus]="data.ticketJustAdded"
					formControlName="activityName"
				></ticket-description>
			</div>
			<div class="ticket-edit-container__crew-days">
				<!-- CREW AND DAYS HERE -->
				<ticket-crew
					class="crew-and-days-width"
					formControlName="liveCrewSize"
					automationId="liveCrewSize"
				></ticket-crew>
				<ticket-duration
					class="crew-and-days-width"
					formControlName="liveDurationDays"
					automationId="liveDurationDays"
				></ticket-duration>
			</div>
			<!-- External Schedule ID -->
			<div
				*ngIf="isAdmin$ | async"
				class="grid-ticket-subcomponent-wrapper__input ticket-edit-container__task-external-schedule"
			>
				<ticket-ext-sched-id formControlName="externalScheduleId"></ticket-ext-sched-id>
			</div>
			<!-- WBS Code -->
			<div
				*ngIf="(isAdmin$ | async) && (isWbsCodeEnabled$ | async)"
				class="grid-ticket-subcomponent-wrapper__input ticket-edit-container__task-wbs-code"
			>
				<ticket-wbs-code formControlName="wbsCode"></ticket-wbs-code>
			</div>
			<!-- Custom Fields -->
			<!-- Caution: The code within [ngClass] is for appropriately adjusting UI elements -->
			<!-- This is necessary because certain fields (external schedule ID and WBS code) are dynamically displayed based on the involved values. -->
			<!-- If future fields affect this as well, consider pushing a different strategy since this is not easily mantainable -->
			<custom-fields-list
				#customFields
				[editedTicketId]="editedTicketId$ | async"
				[combinedEdit$]="ticketEditService.combinedEdit$"
				[ticketEditCombinedData]="this.data"
				[customFieldsApi]="planState.customFieldsApi"
				[isFloatingTickets]="(planState.floatingTickets.editTicket$ | async)?.size ? true : false"
				[customFieldsListFormGroup]="customFieldsListFormGroup"
				[saveCustomFieldsRequested$]="saveCustomFieldsRequested$.asObservable()"
				[cancelCustomFieldsRequested$]="cancelCustomFieldsRequested$.asObservable()"
				[allowList$]="planState.allowedProjectLinks$"
				[planId]="planState.planId"
				[ngClass]="{
					'task-edit-custom-fields-list-with-wbs-code': (isAdmin$ | async) && (isWbsCodeEnabled$ | async),
					'task-edit-custom-fields-list-with-external-schedule': isAdmin$ | async,
					'task-edit-custom-fields-list-without-external-schedule': !(isAdmin$ | async)
				}"
			>
				<!-- Extra padding at the end which is useful if the end of the custom fields have dropdowns that expand down into this area -->
				<div
					*ngIf="customFields.addExtraScrollWindowSpace$ | async"
					class="extra-space-at-bottom-of-edit-ui"
				></div>
			</custom-fields-list>
		</div>
	</div>

	<variance-prompt
		*ngIf="editForm.contains('variance')"
		[formControl]="varianceControl"
		[variance$]="planState.variances.list$"
		[shake$]="planState.shakeVariancePrompt$"
		[showCancelButton]="false"
		[saveButtonText]="'Close'"
		(done)="variancePromptSaved($event)"
		(canceled)="variancePromptCanceled()"
		class="variance-prompt--edit-shifted"
	>
	</variance-prompt>

	<!-- Footer -->
	<ticket-footer
		[actualPhase]="data.data.actualPhase"
		[showActualizedToggle]="
			(hasAccess$ | async) &&
			data.ticketsInPlanRegion &&
			data.data.side != 'pull' &&
			data.data.actualPhase !== 'actualizing'
		"
		[formDirty]="editForm.dirty"
		[formValid]="isFormValid"
		[isReadOnly]="isReadOnlyTicket || (planAccess$ | async) === PlanAccessLevel.archived"
		[isPlanRegion]="data.ticketsInPlanRegion"
		(onActualChange)="updateActualPhase($event)"
		(canceled)="cancel()"
		(saved)="save()"
	>
	</ticket-footer>
</div>
